import AddIcon from "@mui/icons-material/Add";
import {
	Box,
	Pagination,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	styled,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, useEffect, useState } from "react";
import EmailButtonIcon from "../../../assets/images/icons/EmailButtonIcon";
import { ButtonIcons } from "../../../layout/TableLayout/TableLayout";
import BriaButton from "../BriaButton/BriaButton";
import LoadingPlaceholder from "../LoadingPlaceholder/LoadingPlaceholder";
import styles from "./BriaTable.module.scss";

export interface FilterBySearch<T = any> {
	text: string;
	fields: (keyof T)[];
}

export type FilterByOptions<T = any> = {
	search?: FilterBySearch<T>;
	filters?: Partial<T>;
};

export type SortByOptions<T = any> = {
	field: keyof T;
	order?: "asc" | "desc";
};

export type PaginationOptions<T = any> = {
	rowsPerPage: number;
	totalRows: number;
	loadNextPage: (pageNumber: number, filterBy?: FilterByOptions<T>) => Promise<any>;
};

interface IProps {
	headerArray: any;
	rows: any;
	filterBy?: FilterByOptions;
	paginationOptions?: PaginationOptions;
	loading?: boolean;
	loadingRow?: { data: any; column: string };
	disableBottomButton?: boolean;
	hideBottomButton?: boolean;
	bottomButtonIconType?: ButtonIcons;
	bottomButtonText?: string;
	bottomButtonClick?: () => void;
	customTable?: any;
	// Optional per-column styling
	columnClassNames?: Record<string, string>;
	columnWidths?: Record<string, number | string>;
	// Optional explicit data key order to align header cells with body cells
	columnOrderKeys?: string[];
	// Optional override for container min-height (defaults to SCSS $tableMinHeight)
	minTableHeight?: number | string;
	// Optional custom header content per column key
	headerComponents?: Record<string, React.ReactNode>;
	// Optional class to override loading cell wrapper styling
	loadingCellClassName?: string;
	// Optional sticky header for inner table scroll
	stickyHeader?: boolean;
}

const BriaTable = ({
	headerArray,
	rows,
	filterBy,
	paginationOptions,
	loading = false,
	loadingRow,
	hideBottomButton,
	disableBottomButton,
	bottomButtonIconType,
	bottomButtonText,
	bottomButtonClick,
	customTable,
	columnClassNames,
	columnWidths,
	columnOrderKeys,
	minTableHeight,
	headerComponents,
	loadingCellClassName,
	stickyHeader,
}: IProps) => {
	const [page, setPage] = useState(0);
	const pagesCount = paginationOptions ? Math.ceil(paginationOptions.totalRows / paginationOptions.rowsPerPage!) : 1;
	const handleChangePage = async (_event: ChangeEvent<unknown> | null, page: number) => {
		setPage(page - 1);
		await paginationOptions?.loadNextPage(page, filterBy);
	};

	useEffect(() => {
		if (filterBy && rows.length) {
			setPage(0);
		}
	}, [filterBy]);

	return (
		<Box className={styles.container}>
			<TableContainer
				// sx={{ height: paginationOptions ? `${paginationOptions?.rowsPerPage * 63.38}px` : "auto" }}
				className={`${styles.tableContainer} ${stickyHeader ? styles.stickyHeader : ""}`}
				component={Paper}
				sx={minTableHeight ? { minHeight: `${minTableHeight} !important` } : undefined}
			>
				<LoadingPlaceholder className={styles.loadingPlaceholder} isLoading={loading}>
					{customTable ? (
						React.createElement(customTable, { headers: headerArray, rows: rows })
					) : (
						<Table
							stickyHeader={!!stickyHeader}
							aria-label="simple table"
							sx={{
								tableLayout: columnOrderKeys || columnWidths || columnClassNames ? "fixed" : undefined,
								width: columnOrderKeys || columnWidths || columnClassNames ? "100%" : undefined,
							}}
						>
							<TableHead>
								<TableRow>
									{headerArray.map((el: any, index: number) => {
										// Use provided columnOrderKeys if available; otherwise, derive from first row
										const derivedKeys = rows && rows.length ? Object.keys(rows[0].data) : [];
										const columnKey = columnOrderKeys?.[index] ?? derivedKeys[index];
										return (
											<TableCell
												key={index}
												className={columnKey ? columnClassNames?.[columnKey] : undefined}
												sx={
													columnKey && columnWidths?.[columnKey] !== undefined
														? { width: columnWidths[columnKey] as any }
														: undefined
												}
											>
												{columnKey && headerComponents?.[columnKey] !== undefined
													? headerComponents[columnKey]
													: el}
											</TableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((row: any, index: number) => (
									<TableRow
										key={index}
										sx={{
											backgroundColor: row["rowStyle"]?.backgroundColor,
											"&:last-child td, &:last-child th": { border: 0 },
										}}
									>
										{Object.entries(row.data).map(([column, v]: any, index: any) => {
											return (
												<TableCell
													key={index}
													align="left"
													className={columnClassNames?.[column]}
													sx={
														columnWidths?.[column] !== undefined
															? { width: columnWidths[column] as any }
															: undefined
													}
												>
													<LoadingPlaceholder
														className={loadingCellClassName || styles.loadingCell}
														size="25px"
														isLoading={
															!!(
																row.key &&
																column === loadingRow?.column &&
																row.key === loadingRow?.data.id
															)
														}
													>
														{v}
													</LoadingPlaceholder>
												</TableCell>
											);
										})}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</LoadingPlaceholder>
			</TableContainer>
			{bottomButtonText && !hideBottomButton && (
				<BriaButton
					onClick={bottomButtonClick}
					buttonType="textSmall"
					disabled={disableBottomButton}
					className={styles.bottomBtn}
				>
					{bottomButtonIconType === "email" ? (
						<EmailButtonIcon className={styles.emailButtonIcon} />
					) : bottomButtonIconType === "plus" ? (
						<AddIcon />
					) : (
						<></>
					)}
					{bottomButtonText}
				</BriaButton>
			)}
			{paginationOptions && pagesCount > 1 && (
				<Box
					marginTop={bottomButtonText ? "0" : "4vh"}
					display={"flex"}
					justifyContent={"center"}
					alignItems={"center"}
				>
					<TablePagination
						classes={{
							root: styles.paginationRoot,
							ul: styles.ul,
						}}
						onChange={handleChangePage}
						sx={{
							"&.MuiButtonBase-root-MuiPaginationItem-root.Mui-selected": {
								color: "#5300c9",
							},
						}}
						count={pagesCount}
						page={page + 1}
					/>
				</Box>
			)}
		</Box>
	);
};

export default observer(BriaTable);

const TablePagination = styled(Pagination)(({ theme }) => ({
	ul: {
		"li:not(:last-child,:first-of-type)": {
			button: {
				fontWeight: 600,
				fontFamily: "Montserrat",
				fontSize: "10.6px",
				width: 24,
				minWidth: "unset",
				height: 24,
				borderRadius: "50%",
				backgroundColor: "#E7E7E7",
				"&.Mui-selected": {
					color: "white",
					backgroundColor: theme.palette.primary.light,
				},
			},
		},
	},
}));
